/**
 * Currency utilities and popular currencies list
 */

export const POPULAR_CURRENCIES = [
  { code: 'EUR', symbol: '€', name: 'Euro (€ EUR)', flag: '🇪🇺', isStandard: true },
  { code: 'USD', symbol: '$', name: 'US Dollar ($ USD)', flag: '🇺🇸', isStandard: true },
  { code: 'GBP', symbol: '£', name: 'British Pound (£ GBP)', flag: '🇬🇧', isStandard: true },
  { code: 'CHF', symbol: 'CHF', name: 'Swiss Franc (CHF)', flag: '🇨🇭', isStandard: true },
  { code: 'CAD', symbol: 'CA$', name: 'Canadian Dollar (CA$ CAD)', flag: '🇨🇦', isStandard: true },
  { code: 'AUD', symbol: 'A$', name: 'Australian Dollar (A$ AUD)', flag: '🇦🇺', isStandard: true },
  { code: 'AED', symbol: 'AED', name: 'UAE Dirham (AED)', flag: '🇦🇪', isStandard: true },
  { code: 'TRY', symbol: '₺', name: 'Turkish Lira (₺ TRY)', flag: '🇹🇷', isStandard: true },
  { code: 'AFN', symbol: '؋', name: 'Afghan Afghani (؋ AFN)', flag: '🇦🇫', isStandard: true },
  { code: 'SAR', symbol: 'SAR', name: 'Saudi Riyal (SAR)', flag: '🇸🇦', isStandard: true },
  { code: 'SEK', symbol: 'kr', name: 'Swedish Krona (SEK)', flag: '🇸🇪', isStandard: true },
  { code: 'NOK', symbol: 'kr', name: 'Norwegian Krone (NOK)', flag: '🇳🇴', isStandard: true },
  { code: 'DKK', symbol: 'kr', name: 'Danish Krone (DKK)', flag: '🇩🇰', isStandard: true },
  { code: 'INR', symbol: '₹', name: 'Indian Rupee (₹ INR)', flag: '🇮🇳', isStandard: true },
  { code: 'JPY', symbol: '¥', name: 'Japanese Yen (¥ JPY)', flag: '🇯🇵', isStandard: true },
];

export const CURRENCY_SYMBOLS = {
  EUR: '€',
  USD: '$',
  GBP: '£',
  CHF: 'CHF',
  CAD: 'CA$',
  AUD: 'A$',
  AED: 'AED',
  TRY: '₺',
  AFN: '؋',
  SAR: 'SAR',
  SEK: 'kr',
  NOK: 'kr',
  DKK: 'kr',
  INR: '₹',
  JPY: '¥',
};

const STORAGE_CUSTOM_CURRENCIES_KEY = 'bamyan_custom_currencies';
const STORAGE_DEFAULT_CURRENCY_KEY = 'bamyan_default_currency';
const STORAGE_DEFAULT_VAT_KEY = 'bamyan_default_vat_rate';

export function getCustomCurrencies() {
  if (typeof window === 'undefined') return [];
  try {
    const raw = localStorage.getItem(STORAGE_CUSTOM_CURRENCIES_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch (e) {
    return [];
  }
}

export function saveCustomCurrency(currency) {
  if (typeof window === 'undefined' || !currency || !currency.code) return;
  const existing = getCustomCurrencies().filter(
    (c) => c.code.toUpperCase() !== currency.code.toUpperCase()
  );
  const updated = [
    ...existing,
    {
      code: currency.code.toUpperCase().trim(),
      symbol: currency.symbol?.trim() || currency.code.toUpperCase().trim(),
      name: currency.name?.trim() || `${currency.code.toUpperCase()} Currency`,
      flag: currency.flag?.trim() || '🌐',
      isCustom: true,
    },
  ];
  localStorage.setItem(STORAGE_CUSTOM_CURRENCIES_KEY, JSON.stringify(updated));
}

export function deleteCustomCurrency(code) {
  if (typeof window === 'undefined' || !code) return;
  const existing = getCustomCurrencies().filter(
    (c) => c.code.toUpperCase() !== code.toUpperCase()
  );
  localStorage.setItem(STORAGE_CUSTOM_CURRENCIES_KEY, JSON.stringify(existing));
}

export function getAllCurrencies() {
  const custom = getCustomCurrencies();
  const popularCodes = new Set(POPULAR_CURRENCIES.map((c) => c.code));
  const uniqueCustom = custom.filter((c) => !popularCodes.has(c.code));
  return [...POPULAR_CURRENCIES, ...uniqueCustom];
}

export function getDefaultCurrency() {
  if (typeof window === 'undefined') return 'EUR';
  return localStorage.getItem(STORAGE_DEFAULT_CURRENCY_KEY) || 'EUR';
}

export function setDefaultCurrency(code) {
  if (typeof window === 'undefined' || !code) return;
  localStorage.setItem(STORAGE_DEFAULT_CURRENCY_KEY, code.toUpperCase().trim());
}

export function getDefaultVatRate() {
  if (typeof window === 'undefined') return '7.00';
  return localStorage.getItem(STORAGE_DEFAULT_VAT_KEY) || '7.00';
}

export function setDefaultVatRate(rate) {
  if (typeof window === 'undefined' || rate == null) return;
  localStorage.setItem(STORAGE_DEFAULT_VAT_KEY, String(rate).trim());
}

export function getCurrencySymbol(code = 'EUR') {
  if (!code) return '€';
  const upper = String(code).trim().toUpperCase();
  if (CURRENCY_SYMBOLS[upper]) return CURRENCY_SYMBOLS[upper];

  const custom = getCustomCurrencies();
  const found = custom.find((c) => c.code.toUpperCase() === upper);
  if (found && found.symbol) return found.symbol;

  return `${upper} `;
}

export function formatCurrency(amount, code = 'EUR') {
  const num = typeof amount === 'number' ? amount : parseFloat(amount);
  const valid = isNaN(num) ? 0 : num;
  const symbol = getCurrencySymbol(code);
  return `${symbol}${valid.toFixed(2)}`;
}
